import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "services";
import * as d3 from "d3";
import { getNodeColor } from "helpers/graph";

const graphAdapter = createEntityAdapter();
const initialState = graphAdapter.getInitialState({
  assetId: null,
  fetchGraphStatus: "idle",
  fetchGraphErrorMessage: "",
  fetchGraphFiltersStatus: "idle",
  fetchGraphFiltersErrorMessage: "",
  updateGraphStatus: "idle",
  data: null,
  savedZoomPan: { k: 1, x: 0, y: 0 },
  warmupTicks: 100,
  initZoomPan: false,
  searchedIds: [],
  searchTerm: "",
  filters: [],
  date: "",
  minDate: null,
  maxDate: null,
  fetchMinMaxDateStatus: "idle",
  colors: {},
  selectedNode: null,
});

export const fetchGraph = createAsyncThunk(
  "assetGraph/fetchGraph",
  async (
    { entityId, assetId, cartographyId },
    { rejectWithValue, getState }
  ) => {
    const query = `/entities/${entityId}/assets/${assetId}/graph?direction=out&cartographyId=${cartographyId}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchFilters = createAsyncThunk(
  "assetGraph/fetchFilters",
  async (
    { entityId, assetId, cartographyId },
    { rejectWithValue, getState }
  ) => {
    const query = `/entities/${entityId}/assets/${assetId}/filters/list?cartographyId=${cartographyId}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const graphSlice = createSlice({
  name: "assetGraph",
  initialState,
  reducers: {
    setData: (state, { payload }) => {
      const { nodes, links } = payload;
      // have to remove source/target attributes that reference node objects directly
      const _links = links.map((link) => {
        return {
          id: link.id,
          source: link.source.id,
          target: link.target.id,
        };
      });
      state.data = JSON.parse(JSON.stringify({ nodes, links: _links }));
      return state;
    },
    setWarmupTicks: (state, { payload }) => {
      state.warmupTicks = payload;
      return state;
    },
    refreshFetchGraphStatus: (state) => {
      state.fetchGraphStatus = "idle";
      return state;
    },
    setSavedZoomPan: (state, { payload }) => {
      state.savedZoomPan = payload;
      return state;
    },
    setInitZoomPan: (state, { payload }) => {
      state.initZoomPan = payload;
      return state;
    },
    resetSearchedIds: (state) => {
      state.searchedIds = [];
      return state;
    },
    resetSearchTerm: (state) => {
      state.searchTerm = "";
      return state;
    },
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.searchTerm = searchTerm;
      return state;
    },
    updateSearchedIds: (state, { payload }) => {
      const { searchTerm: rawSearchTerm } = payload;
      const searchTerm = rawSearchTerm.toLowerCase();
      state.searchedIds = state.data.nodes
        .filter((e) =>
          e.rawData
            ? e.rawData.toLowerCase().includes(searchTerm) ||
              e.value.toLowerCase().includes(searchTerm)
            : e.value.toLowerCase().includes(searchTerm)
        )
        .map((e) => e.id);
      return state;
    },
    updateAssetId: (state, { payload }) => {
      const { assetId } = payload;
      state.assetId = assetId;
      return state;
    },
    refreshFetchFilterStatus: (state) => {
      state.fetchGraphFiltersStatus = "idle";
      return state;
    },
    filterToggled: (state, { payload }) => {
      const { filterType } = payload;
      const filters = state.filters.map((f) => {
        if (f.type !== filterType) {
          return f;
        }
        return {
          ...f,
          toggled: !f.toggled,
        };
      });
      state.filters = filters;
      return state;
    },
    setSelectedNode: (state, { payload }) => {
      state.selectedNode = payload;
      return state;
    },
    resetGraphAssetState: () => initialState,
  },
  extraReducers: {
    [fetchGraph.pending]: (state, { payload }) => {
      state.fetchGraphStatus = "loading";
    },
    [fetchGraph.fulfilled]: (state, { payload }) => {
      state.data = payload.data;
      state.fetchGraphStatus = "success";
    },
    [fetchGraph.rejected]: (state, { payload }) => {
      state.fetchGraphErrorMessage = payload.message;
      state.fetchGraphStatus = "error";
    },
    [fetchFilters.pending]: (state) => {
      state.fetchGraphFiltersStatus = "loading";
    },
    [fetchFilters.fulfilled]: (state, { payload }) => {
      state.filters = payload.data.map((t, i) => {
        const myColor = i > 30 ? "#fa7e61" : d3.interpolateCool(i / 25);
        if (!state.colors[t.value]) {
          state.colors[t.value] = getNodeColor(myColor, "#fff");
        }
        return {
          count: t.count,
          type: t.value,
          toggled: false,
        };
      });
      state.fetchGraphFiltersStatus = "success";
    },
    [fetchFilters.rejected]: (state, { payload }) => {
      state.fetchGraphFiltersStatus = "error";
      state.fetchGraphFiltersErrorMessage = payload.message;
    },
  },
});

export const {
  updateSearchedIds,
  updateAssetId,
  resetSearchTerm,
  updateSearchTerm,
  resetGraphAssetState,
  filterToggled,
  resetSearchedIds,
  refreshFetchFilterStatus,
  refreshFetchGraphStatus,
  setSelectedNode,
  setData,
  setSavedZoomPan,
  setInitZoomPan,
  setWarmupTicks,
} = graphSlice.actions;

export default graphSlice.reducer;
